"use client";

import { useEffect, useState } from "react";
import { Button } from "@dionatanbig2be/big2be-ui";
import { FaApple } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { getApiErrorMessage } from "@/shared/helpers/errors";
import { AuthError } from "../services/auth.session";
import { toast } from "@/shared/components";
import { useAuth } from "../hooks/useAuth";
import { useAuthModal } from "../stores/useAuthModal";
import { useAuthFormFields } from "@/shell/hooks/useBootstrap";
import type * as FirebaseSdk from "@/shared/firebase";
import type { SocialSignInResult } from "@/shared/firebase";
import type { Facilitator } from "../types/auth-form-fields.dto";

const FACILITATORS = ["google", "apple"] as const;
const LABELS: Record<Facilitator, string> = { google: "Google", apple: "Apple" };

type FirebaseModule = typeof FirebaseSdk;

/**
 * O SDK do Firebase (~110 KB) só é baixado quando os botões sociais aparecem,
 * fora do bundle de toda página. O carregamento começa assim que eles montam,
 * para o clique chamar o popup SÍNCRONO: um `await` entre o gesto do usuário e
 * o `signInWithPopup` faz o Safari bloquear o popup.
 */
const loadFirebase = () => import("@/shared/firebase");

/** Popup do Firebase por provedor — devolve null quando o usuário cancela. */
function signIn(firebase: FirebaseModule, provider: Facilitator): Promise<SocialSignInResult | null> {
    return provider === "google" ? firebase.signInWithGoogle() : firebase.signInWithApple();
}

/** "João da Silva" → { firstName: "João", lastName: "da Silva" }. */
function splitDisplayName(displayName: string | null): { firstName?: string; lastName?: string } {
    const parts = (displayName ?? "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return {};
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") || undefined };
}

interface AuthFacilitatorsProps {
    facilitators: Record<Facilitator, { visible: boolean }>;
    actionLabel: "login" | "register";
    disabled?: boolean;
    onSuccess?: () => void;
    onError: (message: string) => void;
}

/**
 * Botões de login/cadastro social — visibilidade vem da config do cliente
 * (facilitators.google/apple.visible em login_form_fields/register_form_fields).
 *
 * Fluxo: popup do Firebase → troca o idToken por sessão nossa via
 * POST /api/auth/social (a nest associa o cliente pelo e-mail).
 * Login e cadastro passam pelo mesmo caminho — o back decide.
 * Conta social sem cadastro (404) → abre o cadastro pré-preenchido
 * com nome/e-mail vindos do provedor — a menos que o cadastro esteja
 * bloqueado (loja cadastra o cliente por conta própria), caso em que só
 * mostra o erro genérico, sem oferecer criar conta.
 */
export function AuthFacilitators({
    facilitators,
    actionLabel,
    disabled,
    onSuccess,
    onError,
}: AuthFacilitatorsProps) {
    const { loginWithSocial } = useAuth();
    const openRegisterPrefilled = useAuthModal((state) => state.openRegisterPrefilled);
    const registrationBlocked = useAuthFormFields().register.blocked;
    const [loading, setLoading] = useState<Facilitator | null>(null);
    const [firebase, setFirebase] = useState<FirebaseModule | null>(null);
    const visible = FACILITATORS.filter((provider) => facilitators[provider]?.visible);
    const hasVisible = visible.length > 0;

    useEffect(() => {
        if (!hasVisible) return;
        let active = true;
        void loadFirebase().then((loaded) => {
            if (active) setFirebase(loaded);
        });
        return () => {
            active = false;
        };
    }, [hasVisible]);

    if (!hasVisible) return null;

    async function handleSelect(provider: Facilitator) {
        setLoading(provider);
        let result: SocialSignInResult | null = null;
        try {
            // Já carregado no caminho comum; o `await` só pesa num clique
            // feito antes de o SDK terminar de baixar.
            result = await signIn(firebase ?? (await loadFirebase()), provider);
            if (!result) return; // usuário fechou o popup — não é erro

            await loginWithSocial({ provider, idToken: result.idToken });
            onSuccess?.();
        } catch (error) {
            const genericMessage = `Não foi possível continuar com ${LABELS[provider]}. Tente novamente.`;

            // Conta social sem cadastro → abre o cadastro já preenchido, a não
            // ser que o cadastro esteja bloqueado (aí só o erro genérico).
            if (result && error instanceof AuthError && error.status === 404) {
                if (registrationBlocked) {
                    onError(genericMessage);
                    return;
                }
                openRegisterPrefilled({
                    ...splitDisplayName(result.displayName),
                    email: result.email ?? undefined,
                });
                toast.info("Você ainda não tem cadastro. Complete os dados para continuar.");
                return;
            }

            onError(getApiErrorMessage(error, genericMessage));
        } finally {
            setLoading(null);
        }
    }

    return (
        <div className="col-span-2 flex flex-col gap-2">
            {visible.map((provider) => (
                <Button
                    key={provider}
                    type="button"
                    tone={provider === "apple" ? "neutral" : "default"}
                    variant={provider === "apple" ? "solid" : "outline"}
                    width="block"
                    isLoading={loading === provider}
                    disabled={disabled || loading !== null}
                    onClick={() => handleSelect(provider)}
                >
                    {provider === "google" ? <FcGoogle size={18} /> : <FaApple size={18} />}
                    {actionLabel === "login" ? "Continuar com" : "Cadastrar com"} {LABELS[provider]}
                </Button>
            ))}

            <div className="divider text-xs opacity-50">ou</div>
        </div>
    );
}
